import React, { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import ReactTooltip from 'react-tooltip';
import { classNames } from '@geerd-nx/utils';

export interface ISelectMenu<T> {
  data: T[];
  showTooltips?: boolean;
  accessor: keyof T;
  placeholder?: string;
  value: T;
  readonly?: boolean;
  onChange: (property: T) => void;
  [property: string]: unknown;
}
export function SelectMenu<T>({
  data,
  accessor,
  readonly = false,
  onChange,
  placeholder,
  value,
  showTooltips = false,
  ...rest
}: ISelectMenu<T>) {
  return (
    <Listbox
      value={value}
      onChange={
        !readonly
          ? onChange
          : () => {
              return;
            }
      }
    >
      {({ open }) => (
        <div {...rest} className={`mt-1 relative ${rest?.['className'] ?? ''}`}>
          <Listbox.Button
            className={`relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default ${
              readonly
                ? 'bg-gray-100'
                : 'focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
            } sm:text-sm bg-white`}
          >
            <span className="block truncate">
              {
                (value?.[accessor] ||
                  placeholder ||
                  'select options') as React.ReactNode
              }
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            show={!readonly && open && data?.length !== 0}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {data?.length ? (
              <Listbox.Options className="absolute z-10 mt-1 w-full cursor-pointer bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                {React.Children.toArray(
                  data?.map((record) => (
                    <Listbox.Option
                      className={({ active }) =>
                        classNames(
                          active
                            ? 'text-white bg-primary-600'
                            : 'text-gray-900',
                          'cursor-default select-none relative py-2 pl-8 pr-4 text-clip'
                        )
                      }
                      value={record}
                    >
                      {({ selected, active }) => (
                        <>
                          {!showTooltips ? (
                            <span
                              className={classNames(
                                selected ? 'font-semibold' : 'font-normal',
                                'block truncate'
                              )}
                            >
                              {record[accessor] as React.ReactNode}
                            </span>
                          ) : (
                            <span
                              data-tip
                              data-for={record[accessor]}
                              className={classNames(
                                selected ? 'font-semibold' : 'font-normal',
                                'block truncate'
                              )}
                            >
                              {`${record[accessor] ?? 'accessor not found !!'}`}
                              <ReactTooltip
                                id={record[accessor] as string | undefined}
                                place="top"
                                effect="solid"
                              >
                                {record[accessor] as string}
                              </ReactTooltip>
                            </span>
                          )}

                          {selected ? (
                            <span
                              className={classNames(
                                active ? 'text-white' : 'text-primary-600',
                                'absolute inset-y-0 left-0 flex items-center pl-1.5'
                              )}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))
                )}
              </Listbox.Options>
            ) : (
              <Listbox.Options></Listbox.Options>
            )}
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
